import type { ChartSelectorConfig } from '@revelio/core';

import type { SavedQueryDto } from '../../api/saved-queries-api';
import type { PrimaryFilterId } from '../../filters/types/filters';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import {
  isCompositionMetric,
  isPayMetric,
  isPostingMetric,
  isScreenerMetric,
  type MetricValues,
} from '../PageConfigProvider/types/metric';
import { deriveSelectorOverridesFromSavedQuery } from './utils/derive-selector-overrides';
import { getIsFilterEnabled } from './utils/filter-availability';
import { isSelectableChartType } from './utils/is-selectable-chart-type';
import { getAvailableSegment } from './utils/segment-mapping';

export const CHART_SELECTOR_CONFIG: Record<
  SelectableChartType,
  ChartSelectorConfig
> = {
  bar: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: true,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  table: {
    metricMode: 'multi',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: true,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  'stacked-bar': {
    metricMode: 'single',
    showSegment: true,
    forceSegment: true,
    showGroupBySegment: true,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  'grouped-bar': {
    metricMode: 'single',
    showSegment: true,
    forceSegment: true,
    showGroupBySegment: true,
    showFilter: true,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  'diverging-bar': {
    metricMode: 'single',
    showSegment: true,
    forceSegment: true,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  'diverging-bar-combo': {
    metricMode: 'single',
    showSegment: true,
    forceSegment: true,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  treemap: {
    metricMode: 'single',
    showSegment: true,
    forceSegment: false,
    showGroupBySegment: true,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  line: {
    metricMode: 'single',
    showSegment: true,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  box: {
    metricMode: 'single',
    showSegment: true,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: true,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },

  map: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  distribution: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  sankey: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: true,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  chord: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  volume: {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  gauge: {
    metricMode: 'none',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  text: {
    metricMode: 'none',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
  'ai-summary': {
    metricMode: 'single',
    showSegment: false,
    forceSegment: false,
    showGroupBySegment: false,
    showFilter: false,
    showUnitOfAnalysis: false,
    showReportParameters: true,
    showSnowflakeFields: false,
  },
} satisfies Record<SelectableChartType, ChartSelectorConfig>;

export function getChartSelectorConfig(
  chartType: ChartType
): ChartSelectorConfig | null {
  if (!isSelectableChartType(chartType)) return null;
  return CHART_SELECTOR_CONFIG[chartType];
}

/**
 * Resolves the effective chart selector config by narrowing the base
 * chart-type config with metric- and primary-filter-aware rules.
 *
 * This is the single source of truth for which selectors are visible
 * in the ChartBuilderPanel for a given chart+metric+filter combination.
 */
export function resolveChartSelectorConfig({
  chartType,
  metric,
  primaryFilterId,
  savedQuery,
}: {
  chartType: SelectableChartType;
  metric?: MetricValues;
  primaryFilterId?: PrimaryFilterId;
  savedQuery?: SavedQueryDto;
}): ChartSelectorConfig {
  const base = CHART_SELECTOR_CONFIG[chartType];

  // Snowflake saved queries define their own UI constraints (which selectors
  // are visible, metric mode, etc.) via `selectorOverrides` on the query
  // itself. Applying them here bypasses the standard metric-level narrowing
  // below so the query author has full control over the builder panel layout.
  if (savedQuery) {
    const snowflakeOverrides =
      deriveSelectorOverridesFromSavedQuery(savedQuery);
    return { ...base, ...snowflakeOverrides };
  }

  if (!metric) return base;

  const showSegment = (() => {
    if (!base.showSegment) return false;
    if (!primaryFilterId) return base.showSegment;
    return (
      getAvailableSegment({ chartType, primaryFilterId, metric }).length > 0
    );
  })();

  const showGroupBySegment =
    base.showGroupBySegment &&
    (isCompositionMetric(metric) === true ||
      isPayMetric(metric) === true ||
      isScreenerMetric(metric) === true);

  const showFilter = getIsFilterEnabled({
    chartType,
    metric,
    chartSupportsFilter: base.showFilter,
  });

  const plotFilterMenu =
    chartType === 'grouped-bar' && isPostingMetric(metric)
      ? 'top-only'
      : (base.plotFilterMenu ?? 'all');

  return {
    ...base,
    showSegment,
    showGroupBySegment,
    showFilter,
    plotFilterMenu,
  };
}

/**
 * Returns the effective config after resolving metric/filter narrowing
 * and then applying per-item overrides (e.g. from Snowflake saved queries).
 */
export function getEffectiveChartSelectorConfig(
  chartType: ChartType,
  {
    overrides,
    metric,
    primaryFilterId,
  }: {
    overrides?: Partial<ChartSelectorConfig>;
    metric?: MetricValues;
    primaryFilterId?: PrimaryFilterId;
  } = {}
): ChartSelectorConfig | null {
  if (!isSelectableChartType(chartType)) return null;

  const resolved = resolveChartSelectorConfig({
    chartType,
    metric,
    primaryFilterId,
  });
  return overrides ? { ...resolved, ...overrides } : resolved;
}
